/**
 * Gestapeltes Säulendiagramm das jährliche Erlöse nach Tarifquelle zeigt
 * 
 * Verwendet Backend-berechnete Daten aus EnergyRevenuesResponse
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { EnergyRevenuesResponse, PPARevenueDetail } from '../../types';
import { colors } from '../../styles/theme';

interface TariffRevenueChartProps {
  /** Energieerlöse vom Backend (mit PPA-Details) */
  energyRevenues: EnergyRevenuesResponse | null;
  /** Höhe des Charts */
  height?: number;
}

// Nivo erwartet Record<string, string | number>, daher verwenden wir einen einfachen Typ
type ChartDataPoint = Record<string, string | number>;

interface TooltipData {
  eeg: number;
  marktpreis: number;
  ppaDetails: PPARevenueDetail[];
  gesamt: number;
  abzugKommunal: number;
  zukaufPpa: number;
  nettoErloesEur: number;
}

/**
 * Bereitet die Chart-Daten aus den Backend-Erlösen auf
 */
const prepareChartData = (
  energyRevenues: EnergyRevenuesResponse
): {
  data: ChartDataPoint[];
  keys: string[];
  colorMap: Record<string, string>;
  tooltipData: Map<string, TooltipData>;
  ppaLabels: Map<string, string>;
} => {
  const data: ChartDataPoint[] = [];
  const keysSet = new Set<string>();
  const colorMap: Record<string, string> = {};
  const tooltipData = new Map<string, TooltipData>();
  const ppaLabels = new Map<string, string>();

  // Sammle alle PPA-IDs für konsistente Farben
  const allPpaIds: string[] = [];
  energyRevenues.eintraege.forEach(entry => {
    entry.ppa_details?.forEach(ppa => {
      if (!allPpaIds.includes(ppa.ppa_id)) {
        allPpaIds.push(ppa.ppa_id);
        ppaLabels.set(`ppa_${ppa.ppa_id}`, ppa.bezeichnung);
      }
    });
  });

  // Farben zuweisen (aus zentralem Theme)
  colorMap['eeg'] = colors.chart.tariff.eeg;
  colorMap['marktpreis'] = colors.chart.tariff.marktpreis;
  allPpaIds.forEach((ppaId, index) => {
    colorMap[`ppa_${ppaId}`] = colors.chart.tariff.ppa[index % colors.chart.tariff.ppa.length];
  });

  // Daten für jedes Jahr aufbereiten (Balkenhöhe = Netto-Erlös)
  energyRevenues.eintraege.forEach(entry => {
    const dataPoint: ChartDataPoint = { jahr: `${entry.jahr}` };

    // Skalierungsfaktor: Netto/Brutto, damit Balkenhöhe den Netto-Erlös widerspiegelt
    const brutto = entry.erloes_eur || 0;
    const netto = entry.netto_erloes_eur ?? brutto;
    const nettoFaktor = brutto > 0 ? Math.max(0, netto) / brutto : 1;

    // EEG-Erlös (skaliert auf Netto-Anteil)
    if (entry.erloes_eeg > 0) {
      dataPoint['eeg'] = Math.round(entry.erloes_eeg * nettoFaktor);
      keysSet.add('eeg');
    }

    // Individuelle PPA-Erlöse (skaliert auf Netto-Anteil)
    entry.ppa_details?.forEach(ppa => {
      const ppaKey = `ppa_${ppa.ppa_id}`;
      dataPoint[ppaKey] = Math.round(ppa.erloes_eur * nettoFaktor);
      keysSet.add(ppaKey);
    });

    // Marktpreis-Erlös (skaliert auf Netto-Anteil)
    if (entry.erloes_marktpreis > 0) {
      dataPoint['marktpreis'] = Math.round(entry.erloes_marktpreis * nettoFaktor);
      keysSet.add('marktpreis');
    }

    // Tooltip-Daten für dieses Jahr speichern
    tooltipData.set(`${entry.jahr}`, {
      eeg: entry.erloes_eeg,
      marktpreis: entry.erloes_marktpreis,
      ppaDetails: entry.ppa_details || [],
      gesamt: entry.erloes_eur,
      abzugKommunal: entry.abzug_kommunal ?? 0,
      zukaufPpa: entry.zukauf_ppa ?? 0,
      nettoErloesEur: entry.netto_erloes_eur ?? entry.erloes_eur,
    });

    data.push(dataPoint);
  });

  // Keys in sinnvoller Reihenfolge: EEG, Marktpreis, PPAs
  const orderedKeys: string[] = [];
  if (keysSet.has('eeg')) orderedKeys.push('eeg');
  if (keysSet.has('marktpreis')) orderedKeys.push('marktpreis');
  allPpaIds.forEach(ppaId => {
    const ppaKey = `ppa_${ppaId}`;
    if (keysSet.has(ppaKey)) orderedKeys.push(ppaKey);
  });

  return { data, keys: orderedKeys, colorMap, tooltipData, ppaLabels };
};

/**
 * Formatiert Zahlen im deutschen Währungsformat
 */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Erzeugt Labels für die Legende
 */
const getKeyLabel = (key: string, ppaLabels: Map<string, string>): string => {
  if (key === 'eeg') return 'EEG-Tarif';
  if (key === 'marktpreis') return 'Marktpreis';
  return ppaLabels.get(key) || 'PPA';
};

const TariffRevenueChart: React.FC<TariffRevenueChartProps> = ({
  energyRevenues,
  height = 350,
}) => {
  // Hover-State für Dimming-Effekt
  const [hoveredYear, setHoveredYear] = React.useState<string | null>(null);

  // Chart-Daten aus Backend-Erlösen aufbereiten
  const { data, keys, colorMap, tooltipData, ppaLabels } = React.useMemo(
    () => energyRevenues
      ? prepareChartData(energyRevenues)
      : {
        data: [] as ChartDataPoint[],
        keys: [] as string[],
        colorMap: {} as Record<string, string>,
        tooltipData: new Map<string, TooltipData>(),
        ppaLabels: new Map<string, string>()
      },
    [energyRevenues]
  );

  // Wenn keine Daten vorhanden
  if (!energyRevenues || data.length === 0 || keys.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{
          color: "text.secondary"
        }}>
          Keine Erlösdaten vorhanden. Bitte konfiguriere zuerst die Tarife und Ertragsprognosen.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Chart - onMouseLeave auf Container setzt Dimming zurück wenn Maus Chart verlässt */}
      <Box sx={{ height }} onMouseLeave={() => setHoveredYear(null)}>
        <ResponsiveBar
          data={data}
          keys={keys}
          indexBy="jahr"
          groupMode="stacked"
          margin={{ top: 20, right: 20, bottom: 40, left: 70 }}
          padding={0.2}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={(bar) => colorMap[bar.id] || '#ccc'}
          borderRadius={8}
          borderWidth={0}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
            tickRotation: 0,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 8,
            tickRotation: 0,
            format: (value) => `${(value / 1000).toLocaleString('de-DE')}k`,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          enableLabel={false}
          // Hover-Event für Dimming-Effekt - nur onMouseEnter, kein onMouseLeave
          // (verhindert "Flash" beim Wechsel zwischen Balken)
          onMouseEnter={(bar) => setHoveredYear(bar.indexValue as string)}
          // Custom Layer für Dimming-Overlay
          layers={[
            'grid',
            'axes',
            'bars',
            'markers',
            'legends',
            'annotations',
            // Dimming-Overlay für nicht-gehoverter Balken
            ({ bars }) => (
              <>
                {bars.map((bar) => {
                  const barYear = bar.data.indexValue as string;
                  const shouldDim = hoveredYear !== null && hoveredYear !== barYear;

                  return (
                    <rect
                      key={`dim-${bar.key}`}
                      x={bar.x}
                      y={bar.y}
                      width={bar.width}
                      height={bar.height}
                      fill="white"
                      opacity={shouldDim ? 0.5 : 0}
                      rx={8}
                      ry={8}
                      style={{
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: 'none',
                      }}
                    />
                  );
                })}
              </>
            ),
          ]}
          theme={{
            axis: {
              ticks: {
                text: {
                  fontSize: 11,
                  fill: colors.text.secondary,
                },
              },
            },
            grid: {
              line: {
                stroke: colors.chart.stroke,
                strokeWidth: 1,
              },
            },
          }}
          tooltip={({ indexValue }) => {
            const yearData = tooltipData.get(indexValue as string);
            if (!yearData) return null;

            return (
              <Box
                sx={{
                  background: 'white',
                  padding: '12px 16px',
                  border: `1px solid ${colors.border.divider}`,
                  borderRadius: '8px',
                  boxShadow: `0 4px 12px ${colors.shadow.medium}`,
                  minWidth: 300,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Jahr {indexValue}
                </Typography>

                {/* EEG-Erlös */}
                {yearData.eeg > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '2px',
                        backgroundColor: colors.chart.tariff.eeg,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      EEG-Tarif
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(yearData.eeg)}
                    </Typography>
                  </Box>
                )}

                {/* Marktpreis-Erlös */}
                {yearData.marktpreis > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '2px',
                        backgroundColor: colors.chart.tariff.marktpreis,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Marktpreis
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(yearData.marktpreis)}
                    </Typography>
                  </Box>
                )}

                {/* Individuelle PPA-Erlöse */}
                {yearData.ppaDetails.map((ppa: PPARevenueDetail) => (
                  <Box key={ppa.ppa_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '2px',
                        // Farbe aus colorMap holen (konsistent mit Chart)
                        backgroundColor: colorMap[`ppa_${ppa.ppa_id}`] || colors.chart.tariff.ppa[0],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {ppa.bezeichnung}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(ppa.erloes_eur)}
                    </Typography>
                  </Box>
                ))}

                {/* Trennlinie und Brutto */}
                <Box sx={{ borderTop: `1px solid ${colors.border.divider}`, mt: 1, pt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                      Brutto
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(yearData.gesamt)}
                    </Typography>
                  </Box>

                  {/* Abzüge */}
                  {yearData.abzugKommunal > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        Kommunale Beteiligung
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.status.error.main, whiteSpace: 'nowrap' }}>
                        −{formatCurrency(yearData.abzugKommunal)}
                      </Typography>
                    </Box>
                  )}

                  {yearData.zukaufPpa > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        PPA-Zukauf
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.status.error.main, whiteSpace: 'nowrap' }}>
                        −{formatCurrency(yearData.zukaufPpa)}
                      </Typography>
                    </Box>
                  )}

                  {/* Netto-Zeile (nur wenn Abzüge vorhanden) */}
                  {(yearData.abzugKommunal > 0 || yearData.zukaufPpa > 0) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, borderTop: `1px solid ${colors.border.divider}`, pt: 0.75 }}>
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                        Netto
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(yearData.nettoErloesEur)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          }}
          role="application"
          ariaLabel="Erlöse pro Jahr"
        />
      </Box>
      {/* Legende */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 3,
          mt: 2,
          px: 2,
        }}
      >
        {keys.map((key) => (
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '4px',
                backgroundColor: colorMap[key],
              }}
            />
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {getKeyLabel(key, ppaLabels)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TariffRevenueChart;
